import React, { memo, useState, useEffect } from "react";
import MoonLoader from "react-spinners/MoonLoader";
import { IconClock, IconUser, IconArrowRight, IconCheck } from "@tabler/icons-react";
import { ServiceCenterAPI } from "../../services";
import { SESSION_STATUS } from "../../utils/constants";
import { formatDate, formatDuration } from "../../utils/formatters";
import "./SessionHistory.css";

/**
 * Histórico de sessões de atendimento
 */
const SessionHistory = memo(({ contactId }) => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSessions = async () => {
      if (!contactId) return;

      setIsLoading(true);
      try {
        const data = await ServiceCenterAPI.getSessions(contactId);
        setSessions(data.results || data || []);
      } catch (error) {
        console.error("Erro ao carregar histórico:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSessions();
  }, [contactId]);

  // Status da sessão
  const getStatusConfig = (status) => {
    switch (status) {
      case SESSION_STATUS.ACTIVE:
        return { icon: "🟢", label: "Ativo", color: "#06b776" };
      case SESSION_STATUS.TRANSFERRED:
        return { icon: "🔄", label: "Transferido", color: "#06b6d4" };
      case SESSION_STATUS.FINISHED:
        return { icon: "✅", label: "Finalizado", color: "#64748b" };
      default:
        return { icon: "⚪", label: status, color: "#64748b" };
    }
  };

  if (isLoading) {
    return (
      <div className="sc-session-history sc-session-history--loading">
        <MoonLoader size={24} color="#3b82f6" />
        <span>Carregando histórico...</span>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="sc-session-history sc-session-history--empty">
        <span className="sc-session-history__empty-icon">📋</span>
        <span>Nenhum atendimento anterior</span>
      </div>
    );
  }

  return (
    <div className="sc-session-history">
      <div className="sc-session-history__list">
        {sessions.map((session) => {
          const statusConfig = getStatusConfig(session.status);
          const duration = session.ended_at && session.started_at
            ? formatDuration(new Date(session.ended_at) - new Date(session.started_at))
            : null;

          return (
            <div key={session.id} className="sc-session-item">
              {/* Header */}
              <div className="sc-session-item__header">
                <span
                  className="sc-session-item__status"
                  style={{ color: statusConfig.color }}
                >
                  {statusConfig.icon} {statusConfig.label}
                </span>
                <span className="sc-session-item__date">
                  {formatDate(session.started_at)}
                </span>
              </div>

              {/* Atendente */}
              <div className="sc-session-item__attendant">
                <IconUser size={14} />
                <span>{session.attendant?.name || session.attendant?.username || "Sistema"}</span>

                {/* Se foi transferido, mostrar para quem */}
                {session.status === SESSION_STATUS.TRANSFERRED && session.transferred_to && (
                  <>
                    <IconArrowRight size={14} className="sc-session-item__transfer-arrow" />
                    <span>{session.transferred_to.name || session.transferred_to.username}</span>
                  </>
                )}
              </div>

              {/* Duração */}
              {duration && (
                <div className="sc-session-item__duration">
                  <IconClock size={14} />
                  <span>Duração: {duration}</span>
                </div>
              )}

              {/* Departamento */}
              {session.department && (
                <div className="sc-session-item__department">
                  <span className="sc-session-item__department-label">Departamento:</span>
                  <span>{session.department.name}</span>
                </div>
              )}

              {/* Observação de finalização */}
              {session.finish_note && (
                <div className="sc-session-item__note">
                  <strong>Observação:</strong> {session.finish_note}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

SessionHistory.displayName = "SessionHistory";

export default SessionHistory;
